import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    makeStyles,
    Theme,
    createStyles,
    TableFooter,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { PaymentMethodBreakdownDto } from 'api/models';
import { NothingFoundMessage } from 'components/shared';
import { ShareProgressBar } from './shareProgressBar';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        progressContainer: {
            padding: '2px 0px 6px 0px',
            marginLeft: 24,
        },
        textAccent: {
            fontWeight: 600,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        footerText: {
            fontWeight: 700,
            fontSize: 13,
            color: theme.palette.primary.dark,
        },
        textUpper: {
            textTransform: 'uppercase',
        },
        tableContainer: {
            maxHeight: 'calc(100vh - 210px)',
            minHeight: 500,
            overflow: 'auto',
            '& .MuiTableCell-root.MuiTableCell-head': {
                fontWeight: 600,
            },
            '& .MuiTableCell-root.MuiTableCell-head span:last-child': {
                color: theme.palette.grey[500],
                fontWeight: 400,
            },
        },
        refundsText: {
            color: theme.palette.error.main,
        },
    })
);

function formatPrice(amount: number, currency: string): string {
    return `${currency} ${amount.toFixed(2)}`;
}

interface Props {
    payments: PaymentMethodBreakdownDto[];
    refunds: PaymentMethodBreakdownDto[];
    currency: string;
    totalPaymentsCount: number;
    totalAmount: number;
    totalTips: number;
}

export default function PaymentReportTable({
    payments,
    refunds,
    currency,
    totalPaymentsCount,
    totalAmount,
    totalTips,
}: Props) {
    const classes = useStyles();
    const totalRefundsCount = refunds.reduce((acc, refund) => acc + refund.transactionCount, 0);
    const totalRefundsAmount = refunds.reduce(
        (acc, refund) => acc + refund.amount + refund.tipsAmount,
        0
    );

    return (
        <TableContainer className={classes.tableContainer}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{i18n.t('finances.paymentMethod')}</TableCell>
                        <TableCell align="center">
                            <span>{i18n.t('finances.payments')}</span>
                            <br></br>
                            <span>{i18n.t('finances.count')}</span>
                        </TableCell>
                        <TableCell align="center">
                            <span>
                                {i18n.t('finances.grossAmount')} ({currency})
                            </span>
                            <br></br>
                            <span>{i18n.t('finances.received')}</span>
                        </TableCell>
                        <TableCell align="center">
                            <span>{i18n.t('finances.refunds')}</span>
                            <br />
                            <span>
                                {i18n.t('finances.count')} / {i18n.t('finances.amount')} ({currency}
                                )
                            </span>
                        </TableCell>
                        <TableCell align="center">
                            {i18n.t('finances.netAmount')} ({currency})
                        </TableCell>
                        <TableCell align="center" width={200}>
                            {i18n.t('finances.shareOfGrossPayments')}
                        </TableCell>
                        <TableCell align="center">{i18n.t('finances.avgPerTransaction')}</TableCell>
                        <TableCell align="right">
                            <span>
                                {i18n.t('finances.tips')} ({currency})
                            </span>
                            <br />
                            <span>{i18n.t('finances.includedInTotal')}</span>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {payments.length > 0 &&
                        payments.map((row, i) => {
                            const refundsCount =
                                refunds.find((r) => r.method === row.method)?.transactionCount || 0;
                            const refundsAmount =
                                (refunds.find((r) => r.method === row.method)?.amount || 0) +
                                (refunds.find((r) => r.method === row.method)?.tipsAmount || 0);
                            return (
                                <TableRow key={i}>
                                    {/* Method */}
                                    <TableCell>
                                        <Typography variant="body2">
                                            {i18n.t(
                                                `finances.paymentMethodEnum.${row.method.toLowerCase()}`
                                            )}
                                        </Typography>
                                    </TableCell>

                                    {/* Payments */}
                                    <TableCell align="center">{row.transactionCount}</TableCell>

                                    {/* Gross Amount */}
                                    <TableCell align="center">
                                        {formatPrice(row.amount + row.tipsAmount, currency)}
                                    </TableCell>

                                    {/* Refunds */}
                                    <TableCell align="center" className={classes.refundsText}>
                                        {refundsCount} / -{formatPrice(refundsAmount, currency)}
                                    </TableCell>

                                    {/* Net Amount */}
                                    <TableCell align="center">
                                        {formatPrice(
                                            row.amount + row.tipsAmount - refundsAmount,
                                            currency
                                        )}
                                    </TableCell>

                                    {/* Share */}
                                    <TableCell align="center">
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="end"
                                            className={classes.progressContainer}
                                        >
                                            <Typography variant="caption">
                                                {row.sharePercent < 0.01
                                                    ? '<0.01'
                                                    : row.sharePercent}
                                                %
                                            </Typography>
                                            <ShareProgressBar
                                                variant="determinate"
                                                value={row.sharePercent}
                                                style={{ width: '100%' }}
                                            />
                                        </Box>
                                    </TableCell>

                                    {/* Avg per transaction */}
                                    <TableCell align="center">
                                        {formatPrice(row.avgPerTransaction, currency)}
                                    </TableCell>

                                    {/* Tips */}
                                    <TableCell align="right">
                                        {row.tipsAmount > 0
                                            ? formatPrice(row.tipsAmount, currency)
                                            : '--'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                    {payments.length === 0 && (
                        <TableRow>
                            <TableCell height={500} colSpan={100}>
                                <NothingFoundMessage
                                    text={i18n.t('finances.noPaymentsInPeriod')}
                                    maxLength={100}
                                />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>

                {payments.length > 0 && (
                    <TableFooter>
                        <TableRow>
                            <TableCell className={`${classes.footerText} ${classes.textUpper}`}>
                                {i18n.t('common.total')}
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                {totalPaymentsCount}
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                {formatPrice(totalAmount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                {totalRefundsCount} / -{formatPrice(totalRefundsAmount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                {formatPrice(totalAmount - totalRefundsAmount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                100%
                            </TableCell>
                            <TableCell className={classes.footerText} align="center">
                                {formatPrice(totalAmount / totalPaymentsCount, currency)}
                            </TableCell>
                            <TableCell className={classes.footerText} align="right">
                                {formatPrice(totalTips, currency)}
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
}
